import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import swal from "sweetalert";
import Loading from "../../components/loading";
import Pagination from "../../components/Pagination";
import {
    approvePriceRequest,
    getPriceRequests,
    rejectPriceRequest,
} from "../../services/priceRequestService";

const STATUS_OPTIONS = ["PENDING", "APPROVED", "REJECTED"];

const statusBadge = (status) => {
    const styles = {
        PENDING: "bg-yellow-100 text-yellow-800",
        APPROVED: "bg-green-100 text-green-800",
        REJECTED: "bg-red-100 text-red-800",
    };
    return (
        <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                styles[status] || "bg-gray-100 text-gray-800"
            }`}
        >
            {status}
        </span>
    );
};

function PriceRequestList({ t }) {
    const [items, setItems] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [page, setPage] = useState(0);
    const [size] = useState(10);
    const [isLoading, setIsLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("");
    const [filterSearch, setFilterSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [rejectModal, setRejectModal] = useState({ open: false, id: null, isBulk: false });
    const [rejectReason, setRejectReason] = useState("");
    const [rejectLoading, setRejectLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());

    const selectAllRef = useRef(null);
    const { token } = useSelector((state) => state.user);
    const navigate = useNavigate();

    const pendingItems = items.filter((item) => item.requestStatus === "PENDING");
    const isAllSelected =
        pendingItems.length > 0 && pendingItems.every((item) => selectedIds.has(item.id));
    const isIndeterminate =
        !isAllSelected && pendingItems.some((item) => selectedIds.has(item.id));

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = isIndeterminate;
        }
    }, [isIndeterminate]);

    const fetchData = async (currentPage = page) => {
        setIsLoading(true);
        const params = { page: currentPage, size };
        if (filterStatus) params.status = filterStatus;
        if (filterSearch) params.search = filterSearch;

        const response = await getPriceRequests(token, params);
        if (response.success) {
            const { data, total } = response.data;
            setItems(data || []);
            setTotalPages(Math.ceil((total || 0) / size));
        } else {
            navigate("/error", { state: { status: response.status } });
        }
        setIsLoading(false);
    };

    useEffect(() => {
        setSelectedIds(new Set());
        fetchData(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filterStatus, filterSearch]);

    const handlePageClick = (event) => {
        setPage(event.selected);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(0);
        setFilterSearch(searchInput);
    };

    const handleStatusChange = (e) => {
        setPage(0);
        setFilterStatus(e.target.value);
    };

    const handleSelectAll = () => {
        if (isAllSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(pendingItems.map((item) => item.id)));
        }
    };

    const handleSelectOne = (id) => {
        const next = new Set(selectedIds);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setSelectedIds(next);
    };

    const handleApprove = async (id) => {
        const confirmed = await swal({
            title: t("priceRequests.approve_confirm_title"),
            text: t("priceRequests.approve_confirm_text"),
            icon: "warning",
            buttons: { cancel: true, confirm: true },
        });
        if (!confirmed) return;

        const response = await approvePriceRequest(token, id);
        if (response.success) {
            toast.success(t("priceRequests.approve_success"), {
                position: "bottom-right",
                autoClose: 3000,
            });
            fetchData(page);
        } else {
            toast.error(t("priceRequests.action_error"), { position: "bottom-right" });
        }
    };

    const handleApproveAll = async () => {
        const ids = [...selectedIds];
        const confirmed = await swal({
            title: t("priceRequests.approve_all_confirm_title"),
            text: t("priceRequests.approve_all_confirm_text", { count: ids.length }),
            icon: "warning",
            buttons: { cancel: true, confirm: true },
        });
        if (!confirmed) return;

        let successCount = 0;
        for (const id of ids) {
            const res = await approvePriceRequest(token, id);
            if (res.success) successCount++;
        }

        if (successCount === ids.length) {
            toast.success(t("priceRequests.approve_all_success"), {
                position: "bottom-right",
                autoClose: 3000,
            });
        } else {
            toast.warn(`${successCount}/${ids.length} ${t("priceRequests.approve_success")}`, {
                position: "bottom-right",
            });
        }
        setSelectedIds(new Set());
        fetchData(page);
    };

    const openRejectModal = (id) => {
        setRejectReason("");
        setRejectModal({ open: true, id, isBulk: false });
    };

    const openBulkRejectModal = () => {
        setRejectReason("");
        setRejectModal({ open: true, id: null, isBulk: true });
    };

    const closeRejectModal = () => {
        setRejectModal({ open: false, id: null, isBulk: false });
        setRejectReason("");
    };

    const handleRejectSubmit = async () => {
        if (!rejectReason.trim()) {
            toast.warn(t("priceRequests.reject_reason_required"), { position: "bottom-right" });
            return;
        }
        setRejectLoading(true);

        if (rejectModal.isBulk) {
            const ids = [...selectedIds];
            let successCount = 0;
            for (const id of ids) {
                const res = await rejectPriceRequest(token, id, rejectReason);
                if (res.success) successCount++;
            }
            setRejectLoading(false);
            if (successCount === ids.length) {
                toast.success(t("priceRequests.reject_all_success"), {
                    position: "bottom-right",
                    autoClose: 3000,
                });
            } else {
                toast.warn(`${successCount}/${ids.length} ${t("priceRequests.reject_success")}`, {
                    position: "bottom-right",
                });
            }
            setSelectedIds(new Set());
            closeRejectModal();
            fetchData(page);
        } else {
            const response = await rejectPriceRequest(token, rejectModal.id, rejectReason);
            setRejectLoading(false);
            if (response.success) {
                toast.success(t("priceRequests.reject_success"), {
                    position: "bottom-right",
                    autoClose: 3000,
                });
                closeRejectModal();
                fetchData(page);
            } else {
                toast.error(t("priceRequests.action_error"), { position: "bottom-right" });
            }
        }
    };

    return (
        <>
            {isLoading ? (
                <Loading />
            ) : (
                <div className="px-4 py-4 sm:px-6 lg:px-8">
                    <div className="mb-4 sm:flex sm:items-center">
                        <div className="sm:flex-auto">
                            <h1 className="text-xl font-semibold text-gray-900">
                                {t("priceRequests.title")}
                            </h1>
                        </div>
                        {selectedIds.size > 0 && (
                            <div className="mt-2 sm:mt-0 sm:ml-4 flex items-center gap-3">
                                <span className="text-sm text-gray-600">
                                    {t("priceRequests.selected_count", { count: selectedIds.size })}
                                </span>
                                <button
                                    onClick={handleApproveAll}
                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-500 transition-colors"
                                >
                                    {t("priceRequests.approve_all")}
                                </button>
                                <button
                                    onClick={openBulkRejectModal}
                                    className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-500 transition-colors"
                                >
                                    {t("priceRequests.reject_all")}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="mb-4 flex flex-wrap gap-3 items-center">
                        <select
                            value={filterStatus}
                            onChange={handleStatusChange}
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                            <option value="">{t("priceRequests.all_statuses")}</option>
                            {STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s}>
                                    {t(`priceRequests.status.${s.toLowerCase()}`)}
                                </option>
                            ))}
                        </select>

                        <form onSubmit={handleSearch} className="flex gap-2">
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder={t("priceRequests.search_placeholder")}
                                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                            <button
                                type="submit"
                                className="px-4 py-2 bg-gray-700 text-white text-sm rounded-md hover:bg-gray-600"
                            >
                                {t("priceRequests.search")}
                            </button>
                        </form>
                    </div>

                    <div className="flex flex-col mt-2">
                        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-300">
                                        <thead className="text-white bg-gray-700">
                                            <tr>
                                                <th className="py-3 pl-4 pr-2 w-10">
                                                    {pendingItems.length > 0 && (
                                                        <input
                                                            ref={selectAllRef}
                                                            type="checkbox"
                                                            checked={isAllSelected}
                                                            onChange={handleSelectAll}
                                                            className="h-4 w-4 rounded border-gray-400 text-indigo-600 cursor-pointer"
                                                            title={t("priceRequests.select_all")}
                                                        />
                                                    )}
                                                </th>
                                                <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">#</th>
                                                <th className="py-3 pl-4 pr-3 text-left text-sm font-semibold">
                                                    {t("priceRequests.product")}
                                                </th>
                                                <th className="py-3 pl-4 pr-3 text-left text-sm font-semibold">
                                                    {t("priceRequests.supplier")}
                                                </th>
                                                <th className="py-3 pl-4 pr-3 text-left text-sm font-semibold">
                                                    {t("priceRequests.texture")}
                                                </th>
                                                <th className="py-3 pl-4 pr-3 text-left text-sm font-semibold">
                                                    {t("priceRequests.current_price")}
                                                </th>
                                                <th className="py-3 pl-4 pr-3 text-left text-sm font-semibold">
                                                    {t("priceRequests.requested_price")}
                                                </th>
                                                <th className="py-3 pl-4 pr-3 text-left text-sm font-semibold">
                                                    {t("priceRequests.created_at")}
                                                </th>
                                                <th className="py-3 pl-4 pr-3 text-left text-sm font-semibold">
                                                    {t("priceRequests.status_label")}
                                                </th>
                                                <th className="relative py-3 pl-3 pr-4 sm:pr-6">
                                                    <span className="sr-only">{t("priceRequests.actions")}</span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-300">
                                            {items.length > 0 ? (
                                                items.map((item, index) => (
                                                    <tr
                                                        key={item.id}
                                                        className={`hover:bg-gray-50 ${
                                                            selectedIds.has(item.id) ? "bg-indigo-50" : ""
                                                        }`}
                                                    >
                                                        <td className="py-2 pl-4 pr-2">
                                                            {item.requestStatus === "PENDING" && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedIds.has(item.id)}
                                                                    onChange={() => handleSelectOne(item.id)}
                                                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
                                                                />
                                                            )}
                                                        </td>
                                                        <td className="py-2 pl-2 pr-3 text-sm text-gray-900">
                                                            {page * size + index + 1}
                                                        </td>
                                                        <td className="px-2 py-2 text-sm text-gray-700">
                                                            <div>{item.productName || "—"}</div>
                                                            {item.productArticle && (
                                                                <div className="text-xs text-gray-400">{item.productArticle}</div>
                                                            )}
                                                        </td>
                                                        <td className="px-2 py-2 text-sm text-gray-700">
                                                            {item.supplierName || "—"}
                                                        </td>
                                                        <td className="px-2 py-2 text-sm text-gray-700">
                                                            <div className="flex items-center gap-2">
                                                                {item.textureColor && (
                                                                    <span
                                                                        className="inline-block w-4 h-4 rounded-sm border border-gray-200 flex-shrink-0"
                                                                        style={{ backgroundColor: item.textureColor }}
                                                                    />
                                                                )}
                                                                {item.textureName || "—"}
                                                            </div>
                                                        </td>
                                                        <td className="px-2 py-2 text-sm text-gray-700">
                                                            {item.currentPrice != null
                                                                ? `${item.currentPrice} ${item.currencySymbol || ""}`
                                                                : "—"}
                                                        </td>
                                                        <td className="px-2 py-2 text-sm font-medium text-indigo-700">
                                                            {item.requestedPrice != null ? (
                                                                <div className="flex items-center gap-1.5">
                                                                    <span>{item.requestedPrice} {item.currencySymbol || ""}</span>
                                                                    {item.currentPrice != null && item.currentPrice !== 0 && (() => {
                                                                        const pct = ((item.requestedPrice - item.currentPrice) / item.currentPrice) * 100;
                                                                        const up = pct > 0;
                                                                        return (
                                                                            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${up ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                                                                                {up ? "▲" : "▼"}{Math.abs(pct).toFixed(1)}%
                                                                            </span>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            ) : "—"}
                                                        </td>
                                                        <td className="px-2 py-2 text-sm text-gray-700">
                                                            {item.createdDate
                                                                ? new Date(item.createdDate).toLocaleDateString()
                                                                : "—"}
                                                        </td>
                                                        <td className="px-2 py-2 text-sm">
                                                            {statusBadge(item.requestStatus)}
                                                        </td>
                                                        <td className="relative py-2 text-right text-sm font-medium sm:pr-6">
                                                            {item.requestStatus === "PENDING" && (
                                                                <div className="flex justify-end gap-2">
                                                                    <button
                                                                        onClick={() => handleApprove(item.id)}
                                                                        className="px-3 py-1 bg-green-600 text-white rounded-md text-xs hover:bg-green-500"
                                                                    >
                                                                        {t("priceRequests.approve")}
                                                                    </button>
                                                                    <button
                                                                        onClick={() => openRejectModal(item.id)}
                                                                        className="px-3 py-1 bg-red-600 text-white rounded-md text-xs hover:bg-red-500"
                                                                    >
                                                                        {t("priceRequests.reject")}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td
                                                        colSpan={10}
                                                        className="py-8 text-center text-sm text-gray-500"
                                                    >
                                                        {t("priceRequests.empty")}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {totalPages > 0 && (
                        <Pagination
                            handlePageClick={handlePageClick}
                            pageCount={totalPages}
                            forcePage={page}
                        />
                    )}
                </div>
            )}

            {rejectModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            {rejectModal.isBulk
                                ? t("priceRequests.reject_all_modal_title", { count: selectedIds.size })
                                : t("priceRequests.reject_modal_title")}
                        </h3>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            rows={4}
                            placeholder={t("priceRequests.reject_reason_placeholder")}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                        />
                        <div className="mt-4 flex justify-end gap-3">
                            <button
                                onClick={closeRejectModal}
                                disabled={rejectLoading}
                                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                {t("priceRequests.cancel")}
                            </button>
                            <button
                                onClick={handleRejectSubmit}
                                disabled={rejectLoading}
                                className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-500 disabled:opacity-60"
                            >
                                {rejectLoading
                                    ? t("priceRequests.loading")
                                    : t("priceRequests.reject")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default withTranslation()(PriceRequestList);
